// apps/admin/src/components/banners/AdminAnnouncementBanner.tsx
// vg-binding: CONTEXT:decisions (P999.11.D-03/D-14/D-15/D-16 — components/ core dark-token migration)
// vg-binding: INTERFACE-STANDARDS:error-shape (N/A — pure FE restyle, no API surface)
//
// Round-18 (debug 2026-05-23) — admin announcement banner surface.
//
// Renders currently published announcements targeted at the `admin`
// portal in the TopBar center slot (DESIGN.md §7.8). Dismissible
// announcements are remembered in `localStorage` so a refresh doesn't
// re-show what the operator already acknowledged.
//
// Data source: GET /api/v1/announcements/active?portal=admin (auth-only,
// no `announcement:write` gate). Refetches on WS `system.announcement.new`
// so a newly-created announcement shows up without a page reload.

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '@printway/ui';

import {
  listActiveAnnouncements,
  type AnnouncementPortal,
  type AnnouncementPriority,
  type AnnouncementWire,
  type I18nString,
} from '../../api/announcements.api.js';
import { useSystemEventStore } from '../../stores/system-events.store.js';
import { useTranslation } from '../../i18n/useTranslation.js';

const DISMISS_STORAGE_KEY = 'pw-admin-dismissed-announcements';

function readDismissedIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((s): s is string => typeof s === 'string'));
  } catch {
    return new Set();
  }
}

function writeDismissedIds(ids: Set<string>): void {
  try {
    window.localStorage.setItem(
      DISMISS_STORAGE_KEY,
      JSON.stringify(Array.from(ids)),
    );
  } catch {
    // Quota exceeded / privacy mode — non-load-bearing; banner just re-shows.
  }
}

// Tailwind palette per priority — matches the merchant banner colours so
// users see a consistent severity language across portals.
const PRIORITY_STYLES: Record<AnnouncementPriority, string> = {
  critical: 'bg-rose-600 text-white',
  high: 'bg-amber-500 text-white',
  medium: 'bg-sky-600 text-white',
  low: 'bg-pw-border text-white',
};

// Leading glyph kept verbatim per severity; the word part is localized at
// render time via `chrome.banners.announcement.priority.<priority>`.
const PRIORITY_GLYPH: Record<AnnouncementPriority, string> = {
  critical: '⚠',
  high: '⚠',
  medium: 'ℹ',
  low: '·',
};

// Pick the best available localised string. Falls back EN → VI → FR.
function pickLocale(bag: I18nString, locale: 'en' | 'vi' | 'fr'): string {
  const v = bag[locale];
  if (typeof v === 'string' && v.length > 0) return v;
  if (typeof bag.en === 'string' && bag.en.length > 0) return bag.en;
  if (typeof bag.vi === 'string' && bag.vi.length > 0) return bag.vi;
  if (typeof bag.fr === 'string' && bag.fr.length > 0) return bag.fr;
  return '';
}

interface AdminAnnouncementBannerProps {
  /** Locale to render i18n strings in (default: vi to match admin shell). */
  locale?: 'en' | 'vi' | 'fr';
  /** Override portal — defaults to "admin". */
  portal?: AnnouncementPortal;
}

export function AdminAnnouncementBanner({
  locale = 'vi',
  portal = 'admin',
}: AdminAnnouncementBannerProps): JSX.Element | null {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissedIds());
  const [activeIdx, setActiveIdx] = useState(0);

  const query = useQuery<AnnouncementWire[]>({
    queryKey: ['announcements', 'active', portal],
    queryFn: () => listActiveAnnouncements(portal),
    // Cache the feed for 60s — WS push will invalidate on new entries so
    // the banner stays fresh without hammering the BE.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Round-18 — subscribe to the same WS counter the bell uses. When the
  // store counter for `system.announcement.new` bumps, invalidate the
  // feed query so the next render picks up the freshly-created row.
  const announcementBumps = useSystemEventStore(
    (s) => s.counters['system.announcement.new'],
  );
  useEffect(() => {
    if (announcementBumps > 0) {
      void queryClient.invalidateQueries({
        queryKey: ['announcements', 'active', portal],
      });
    }
  }, [announcementBumps, queryClient, portal]);

  const visible = useMemo(() => {
    const rows = query.data ?? [];
    // "dismissible wins" (user decision 2026-07-20): if the author allowed
    // closing, honour the per-user dismiss; only a non-dismissible banner is
    // sticky. force_show is NOT a "can't close" flag — mixing it with
    // dismissible previously hid the X on every portal.
    return rows.filter((a) => (a.dismissible ? !dismissed.has(a.id) : true));
  }, [query.data, dismissed]);

  // Keep the active index in range when the visible list shrinks (e.g.
  // after dismissing the currently-shown banner).
  useEffect(() => {
    if (activeIdx >= visible.length && visible.length > 0) {
      setActiveIdx(0);
    }
  }, [activeIdx, visible.length]);

  if (visible.length === 0) return null;
  const current = visible[Math.min(activeIdx, visible.length - 1)]!;
  const title = pickLocale(current.title_i18n, locale);
  const body = pickLocale(current.body_i18n, locale);
  const cls = PRIORITY_STYLES[current.priority];
  const label = `${PRIORITY_GLYPH[current.priority]} ${t(
    `chrome.banners.announcement.priority.${current.priority}`,
  )}`;

  function dismiss(id: string): void {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    writeDismissedIds(next);
  }

  return (
    <div
      className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium shadow-sm ${cls}`}
      role="status"
      aria-live="polite"
      data-testid="admin-announcement-banner"
      data-priority={current.priority}
      data-announcement-id={current.id}
    >
      <span className="whitespace-nowrap font-semibold">{label}</span>
      <span className="hidden truncate sm:inline" title={title}>
        {title}
      </span>
      {body.length > 0 && (
        <span className="hidden truncate opacity-90 md:inline" title={body}>
          — {body}
        </span>
      )}

      {visible.length > 1 && (
        <span
          className="ml-1 inline-flex items-center gap-1 text-[10px] opacity-80"
          aria-label={t('chrome.banners.announcement.positionAria', {
            current: activeIdx + 1,
            total: visible.length,
          })}
        >
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() =>
              setActiveIdx((i) => (i - 1 + visible.length) % visible.length)
            }
            className="h-auto rounded px-1 py-0 text-current hover:bg-pw-bg-elevated/20 hover:text-current"
            aria-label={t('chrome.banners.announcement.prevAria')}
            data-testid="admin-announcement-banner-prev"
          >
            ‹
          </Button>
          <span>
            {activeIdx + 1}/{visible.length}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => setActiveIdx((i) => (i + 1) % visible.length)}
            className="h-auto rounded px-1 py-0 text-current hover:bg-pw-bg-elevated/20 hover:text-current"
            aria-label={t('chrome.banners.announcement.nextAria')}
            data-testid="admin-announcement-banner-next"
          >
            ›
          </Button>
        </span>
      )}

      {current.dismissible && (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => dismiss(current.id)}
          aria-label={t('chrome.banners.announcement.dismissAria')}
          className="-mr-1 ml-1 h-auto rounded p-0.5 text-current hover:bg-pw-bg-elevated/20 hover:text-current"
          data-testid="admin-announcement-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

export default AdminAnnouncementBanner;
